import { get } from 'lodash'
import {
  toFullDateWithTimezone,
  isToday,
  isTomorrow,
  getTimePeriodWithTimezone,
  toLuxonFromIso,
} from 'utils/dateUtils'

export const hasNoUpcomingLessons = lessons => {
  return !flattenUpcomingLessons(lessons).length
}

export const flattenUpcomingLessons = lessons => [
  ...(lessons?.today ?? []),
  ...(lessons?.tomorrow ?? []),
  ...(lessons?.allOthers ?? []),
]

export const getNextAndLaterSessions = lessons => {
  const upcoming = flattenUpcomingLessons(lessons)

  return {
    nextSession: upcoming[0] || null,
    laterSessions: upcoming.slice(1),
  }
}

export const isUpNext = lessons => {
  const today = lessons?.today ?? []
  const tomorrow = lessons?.tomorrow ?? []
  const allOthers = lessons?.allOthers ?? []

  if (today.length || tomorrow.length) {
    return {
      upNext: [],
      laterLessons: allOthers,
    }
  }
  return {
    upNext: allOthers.length ? [allOthers[0]] : [],
    laterLessons: allOthers.slice(1),
  }
}

export const formatSessionDateBadge = (startDateTime, timezone) => {
  const dateTime = toLuxonFromIso(startDateTime).setZone(timezone || 'local')

  return {
    month: dateTime.toFormat('LLL').toUpperCase(),
    day: dateTime.toFormat('d'),
    weekday: dateTime.toFormat('ccc').toUpperCase(),
  }
}

export const formatSessionDateCompact = (startDateTime, timezone) => {
  const dateTime = toLuxonFromIso(startDateTime).setZone(timezone || 'local')

  return {
    weekday: dateTime.toFormat('ccc').toUpperCase(),
    day: dateTime.toFormat('d'),
  }
}

export const formatSessionDurationMinutes = (startDateTime, endDateTime) => {
  const start = toLuxonFromIso(startDateTime)
  const end = toLuxonFromIso(endDateTime)
  const minutes = Math.round(end.diff(start, 'minutes').minutes)

  return minutes > 0 ? `${minutes}m` : null
}

export const formatSessionTimeShort = (startDateTime, timezone) =>
  toLuxonFromIso(startDateTime)
    .setZone(timezone || 'local')
    .toFormat('h:mm a')

const parseData = lesson => {
  const coachName = get(lesson, 'coach.name', '')
  const facilityName = get(lesson, 'coachFacility.name', '')
  const timezone = lesson.timezone
  const durationLabel = formatSessionDurationMinutes(
    lesson.startDateTime,
    lesson.endDateTime,
  )
  const timeShort = formatSessionTimeShort(lesson.startDateTime, timezone)
  const coachFacilityLine = [coachName, facilityName]
    .filter(Boolean)
    .join(' · ')

  return {
    coachName,
    coachFirstName: coachName.split(' ')[0] || coachName,
    coachExternalId: get(lesson, 'coach.externalId'),
    facilityName,
    coachFacilityLine,
    lessonDate: toFullDateWithTimezone(lesson.startDateTime, timezone),
    lessonTimePeriod: getTimePeriodWithTimezone(
      lesson.startDateTime,
      lesson.endDateTime,
      timezone,
    ),
    lessonCaption: `${lesson.title} with ${coachName}`,
    dateBadge: formatSessionDateBadge(lesson.startDateTime, timezone),
    dateCompact: formatSessionDateCompact(lesson.startDateTime, timezone),
    timeShort,
    durationLabel,
    timePillLabel: durationLabel
      ? `${timeShort} · ${durationLabel}`
      : timeShort,
    laterSummaryLabel: `${timeShort} · Coach ${coachName.split(' ')[0] ||
      coachName}`,
  }
}

export const parseLessons = (lessons, studentId) => {
  return (lessons || []).reduce(
    (acc, cv) => {
      const enrollment = cv.enrollments?.find(
        ({ student }) => student && String(student.id) === String(studentId),
      )

      if (!enrollment || enrollment.isCanceled || cv.isCanceled) return acc

      const parsedLesson = {
        ...cv,
        cancellationCode: enrollment.confirmationCode,
        viewingStudentId: studentId,
        ...parseData(cv),
      }

      if (isToday(cv.startDateTime)) {
        acc.today.push(parsedLesson)
      } else if (isTomorrow(cv.startDateTime)) {
        acc.tomorrow.push(parsedLesson)
      } else {
        acc.allOthers.push(parsedLesson)
      }
      return acc
    },
    { today: [], tomorrow: [], allOthers: [] },
  )
}
